/**
 * Story 91 (jupiter #2831) — the external character of a JOB node.
 *
 * The external character is a property OF THE JOB and of nothing else. A thousand JOB nodes may share
 * the same technology and each one of them is subcontracted, or not, independently of the others. So
 * the only thing that decides is the configurator's mark on that node:
 *
 *     external(job) <=> job.external = TRUE
 *
 * The mark itself is NOT new: `job_reference.extern` has existed end to end for a long time, the
 * planner already groups its waits by it, and this checkbox already wrote it. This story does not
 * redefine the mark — it adds the consequences of carrying it (the five disabled fields of HU-03 and
 * the purple node), plus the lifecycle gating below.
 *
 * S60/R8: this module is the SINGLE owner of the predicate in Jupiter. The Extern checkbox, the five
 * disabled fields and the purple node background all read it from here, so they can never disagree
 * for the same node. Copying the formula into a component is what a test forbids.
 */

/**
 * Why the Extern checkbox is not actionable. One reason per factor of RN21, so the tooltip can name
 * the actual cause instead of showing a generic message (S52/RN24). The values are i18n keys and must
 * exist in the 8 locale files.
 */
export const EXTERN_BLOCK_REASON = {
  NOT_EDITING: 'The workflow is not being edited',
  JOB_ALREADY_STARTED: 'The job has already started',
  FABRICATION_ORDER_CLOSED: 'The fabrication order is closed'
}

/**
 * S40/S41 — whether a technology is declared as subcontracted.
 *
 * ⚠️ THIS IS THE SEED, NOT THE PREDICATE. Its ONLY caller is the factory that builds a JOB node the
 * moment it is dropped on the canvas, to decide the initial value of its mark. Nothing else may call
 * it: the external character of an existing JOB is `isExternalEffective` below, which never looks at
 * the technology. A thousand JOB nodes may share one technology and each is subcontracted, or not, on
 * its own — that is the whole point of the design, and calling this from a component would undo it.
 *
 * ⚠️ NAME COLLISION, and it is deliberate (story 124, contract §7.1). Two different things are called
 * `external` in this editor: `jobReference.external`, the JOB's own mark, which decides EVERYTHING at
 * runtime; and `technology.external`, this seed, which decides nothing after the drop. They are told
 * apart by the object carrying them, not by the name. Same shape `managePreparation` has had since
 * story 113.
 *
 * Strict comparison on purpose: `undefined`, `null`, `'true'` and `1` do NOT seed a mark. That is the
 * fail-safe direction — when in doubt a new node is born unmarked, exactly as it is today — and it
 * also covers the id/code/name skeletons Sun hydrates for some read paths.
 *
 * The flag arrives flat from Sun as a plain boolean; story 91's two-row catalogue never crossed the
 * wire either, and now it does not exist at all.
 *
 * @param {{external?: boolean}} [technology]
 * @returns {boolean}
 */
export const isTechnologyExternal = (technology) => {
  return technology?.external === true
}

/**
 * The predicate itself: the configurator's mark, and nothing else.
 *
 * A missing mark reads as false, exactly as every reader in Sun already treats the nullable column
 * (RN6.1). Note what this is NOT: a function of the technology. Two JOB nodes sharing one technology
 * can differ here, which is the whole point.
 *
 * @param {{external?: boolean}} [jobReference]
 * @returns {boolean}
 */
export const isExternalEffective = (jobReference) => {
  return jobReference?.external === true
}

/**
 * S69/RN30 — pushes the mark of one node onto the ReactFlow node data, so the canvas repaints the
 * moment the checkbox is ticked.
 *
 * Why this exists: the purple is painted from the node's OWN `data.external`, and the node data is
 * only rebuilt when `updateReactFlow` is raised. `UPDATE_JOB_REFERENCE` deliberately does not raise it
 * — a full rebuild re-triggers `fitView` (pan/zoom), which RN09.4/RN09.5 forbid — so without this the
 * colour would only appear after reopening the workflow.
 *
 * Same shape and same reason as `applyJobHighlight` in `bomTree.js`: a pure function that updates one
 * flag in place, never a node data rebuild. The mark is read through the predicate above, so the
 * canvas and the panel can never disagree about what "external" means.
 *
 * @param {Array<{id: string, data: Object}>} [nodes] Current ReactFlow nodes
 * @param {string} targetNodeId Node whose jobReference just changed
 * @param {{external?: boolean}} [jobReference] The updated jobReference
 * @returns {Array<{id: string, data: Object}>} New array; untargeted nodes keep their identity
 */
export const applyExternalMark = (nodes, targetNodeId, jobReference) => {
  const external = isExternalEffective(jobReference)
  return (nodes || []).map((node) => (
    node.id === targetNodeId ? { ...node, data: { ...node.data, external } } : node
  ))
}

/**
 * The three conditions of RN21, in conjunction: `editing AND NOT hasParts AND ofOperable`. Returns
 * whether the checkbox is actionable and, when it is not, which single factor to explain.
 *
 * This gating is the one genuinely new restriction of the story: until now the checkbox looked only at
 * `!editing`, so a JOB could be marked or unmarked after it had already started producing parts, or on
 * a closed fabrication order. It is the same gating Skip uses (#65).
 *
 * Degradation is deliberate and differs factor by factor (RN22):
 * - `hasParts` absent (template context, where Sun does not compute it) reads as false ⇒ does NOT block.
 * - `fabricationOrderOperable` absent (template/subworkflow) reads as operable ⇒ does NOT block. This
 *   follows the editor's own convention, the `true` default of its prop chain, and deliberately not
 *   `isFabricationOrderOperable(undefined)`, which fails safe to `false` for a different purpose.
 *
 * Both degradations point the same way: in a template, where neither fact exists, the checkbox stays
 * editable exactly as it is today. The story takes away no freedom the configurator already had.
 *
 * When several factors block at once the reason is resolved in a fixed order, from the most enclosing
 * precondition to the most specific piece of data, so the tooltip is deterministic.
 *
 * @param {Object} params
 * @param {{external?: boolean, hasParts?: boolean}} [params.jobReference]
 * @param {boolean} [params.editing]
 * @param {boolean} [params.fabricationOrderOperable]
 * @returns {{actionable: boolean, blockReason: string}}
 */
export const resolveExternCheckboxGating = ({ jobReference, editing, fabricationOrderOperable = true } = {}) => {
  if (editing !== true) {
    return { actionable: false, blockReason: EXTERN_BLOCK_REASON.NOT_EDITING }
  }
  if (jobReference?.hasParts === true) {
    return { actionable: false, blockReason: EXTERN_BLOCK_REASON.JOB_ALREADY_STARTED }
  }
  if (fabricationOrderOperable !== true) {
    return { actionable: false, blockReason: EXTERN_BLOCK_REASON.FABRICATION_ORDER_CLOSED }
  }
  return { actionable: true, blockReason: '' }
}
